import type { EventsProductionJobSummaryFragment } from "~/gql/queries/events/productionJobs.generated";
import type { TaskAssignmentSummaryFragment } from "~/gql/queries/events/taskAssignments.generated";
import type { HistoryListRow } from "~mobile/events/helpers/myTaskAssignmentsAdapter";

import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import tenantChanged from "~/store/tenantChanged";

export const EVENTS_SNAPSHOT_ROW_LIMIT = 200;

export const EVENTS_SNAPSHOT_JOB_LIMIT = 300;

const PRE_NULL_UNRESOLVED_JOB_LABEL = "\u2014";

export type EventsSnapshotUserNames = Record<string, string>;

export interface EventsSnapshotJob {
	asOf: number;
	job: EventsProductionJobSummaryFragment;
}

export interface EventsDayTasksSnapshot {
	asOf: number;
	hasMore: boolean;
	nodes: TaskAssignmentSummaryFragment[];
}

export interface EventsTodayHistorySnapshot {
	asOf: number;
	hasMore: boolean;
	nodes: TaskAssignmentSummaryFragment[];
	rows: HistoryListRow[];
}

export interface EventsDaySnapshotState {
	dayTasks?: EventsDayTasksSnapshot;
	jobs: Record<string, EventsSnapshotJob>;
	tenantId?: string;
	todayHistory?: EventsTodayHistorySnapshot;
	userEmail?: string;
	userNames?: EventsSnapshotUserNames;
}

export interface SetEventsDayTasksPayload extends Omit<EventsDayTasksSnapshot, "hasMore"> {
	jobs: EventsProductionJobSummaryFragment[];
	userNames?: EventsSnapshotUserNames;
}

export interface SetEventsTodayHistoryPayload extends EventsTodayHistorySnapshot {
	jobs: EventsProductionJobSummaryFragment[];
}

const initialState: EventsDaySnapshotState = {
	jobs: {}
};

const isSnapshotTime = (asOf: unknown): asOf is number => typeof asOf === "number" && Number.isFinite(asOf);

const isSnapshotJob = (entry: unknown): entry is EventsSnapshotJob => {
	const candidate = entry as Partial<EventsSnapshotJob> | null | undefined;
	return isSnapshotTime(candidate?.asOf) && typeof candidate?.job === "object" && candidate.job !== null;
};

const compareNewestJobFirst = ([, left]: [string, EventsSnapshotJob], [, right]: [string, EventsSnapshotJob]) =>
	right.asOf - left.asOf;

const rememberJobs = (state: EventsDaySnapshotState, jobs: EventsProductionJobSummaryFragment[], asOf: number) => {
	for (const job of jobs) state.jobs[job.productionJobId] = { asOf, job };
	const entries = Object.entries(state.jobs);
	if (entries.length <= EVENTS_SNAPSHOT_JOB_LIMIT) return;
	state.jobs = Object.fromEntries(entries.sort(compareNewestJobFirst).slice(0, EVENTS_SNAPSHOT_JOB_LIMIT));
};

export const resolveAssigneeNames = (
	nodes: readonly TaskAssignmentSummaryFragment[],
	lookupUserName: (email: string) => string | undefined
): EventsSnapshotUserNames => {
	const names: EventsSnapshotUserNames = {};
	for (const { assignedTo } of nodes) {
		if (!assignedTo) continue;
		const email = assignedTo.toLowerCase();
		const name = lookupUserName(email);
		if (name) names[email] = name;
	}
	return names;
};

const isCutAtRowLimit = (rows: readonly unknown[]): boolean => rows.length > EVENTS_SNAPSHOT_ROW_LIMIT;

const restoreHasMore = <Part extends { hasMore: boolean }>(part: Part): Part => ({
	...part,
	hasMore: part.hasMore === true
});

const restoreDayTasks = (dayTasks: EventsDayTasksSnapshot | undefined): EventsDayTasksSnapshot | undefined =>
	dayTasks && isSnapshotTime(dayTasks.asOf) && Array.isArray(dayTasks.nodes) ? restoreHasMore(dayTasks) : undefined;

const isCurrentHistoryRow = (row: unknown): boolean => {
	const candidate = row as Partial<HistoryListRow> | null | undefined;
	return typeof candidate?.loggedInPeriodSeconds === "number" && candidate.jobLabel !== PRE_NULL_UNRESOLVED_JOB_LABEL;
};

const restoreTodayHistory = (
	todayHistory: EventsTodayHistorySnapshot | undefined
): EventsTodayHistorySnapshot | undefined =>
	todayHistory &&
	isSnapshotTime(todayHistory.asOf) &&
	Array.isArray(todayHistory.nodes) &&
	Array.isArray(todayHistory.rows) &&
	todayHistory.rows.every(isCurrentHistoryRow)
		? restoreHasMore(todayHistory)
		: undefined;

const restoreJobs = (jobs: unknown): Record<string, EventsSnapshotJob> =>
	typeof jobs === "object" && jobs !== null
		? Object.fromEntries(Object.entries(jobs).filter(([, entry]) => isSnapshotJob(entry)))
		: {};

const isUserNameEntry = (entry: [string, unknown]): entry is [string, string] => typeof entry[1] === "string";

const restoreUserNames = (userNames: unknown): EventsSnapshotUserNames =>
	Object.fromEntries(Object.entries(userNames ?? {}).filter(isUserNameEntry));

export const restoreEventsDaySnapshot = (persisted: Partial<EventsDaySnapshotState>): EventsDaySnapshotState => ({
	dayTasks: restoreDayTasks(persisted.dayTasks),
	jobs: restoreJobs(persisted.jobs),
	tenantId: persisted.tenantId,
	todayHistory: restoreTodayHistory(persisted.todayHistory),
	userEmail: persisted.userEmail,
	userNames: restoreUserNames(persisted.userNames)
});

export const eventsDaySnapshotSlice = createSlice({
	extraReducers: (builder) => {
		builder.addCase(tenantChanged, () => initialState);
	},
	initialState,
	name: "eventsDaySnapshot",
	reducers: {
		setEventsDayTasks: (state, action: PayloadAction<SetEventsDayTasksPayload>) => {
			const { asOf, jobs, nodes, userNames } = action.payload;
			const savedNodes = nodes.slice(0, EVENTS_SNAPSHOT_ROW_LIMIT);
			state.dayTasks = { asOf, hasMore: isCutAtRowLimit(nodes), nodes: savedNodes };
			state.userNames = resolveAssigneeNames(savedNodes, (email) => userNames?.[email] ?? state.userNames?.[email]);
			rememberJobs(state, jobs, asOf);
		},
		setEventsTodayHistory: (state, action: PayloadAction<SetEventsTodayHistoryPayload>) => {
			const { asOf, hasMore, jobs, nodes, rows } = action.payload;
			state.todayHistory = {
				asOf,
				hasMore: hasMore || isCutAtRowLimit(rows),
				nodes: nodes.slice(0, EVENTS_SNAPSHOT_ROW_LIMIT),
				rows: rows.slice(0, EVENTS_SNAPSHOT_ROW_LIMIT)
			};
			rememberJobs(state, jobs, asOf);
		}
	}
});

export const { setEventsDayTasks, setEventsTodayHistory } = eventsDaySnapshotSlice.actions;

export default eventsDaySnapshotSlice.reducer;
